import { NonVinData, IMutationConfig } from './types';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Returns true only for real backend UUIDs.
 * Custom placeholder IDs (e.g. "custom-subcategory-8") are rejected.
 */
const isRealUUID = (id?: string): id is string => !!id && UUID_REGEX.test(id);

/**
 * Builds optional GraphQL fields for condition and yearOfManufacture.
 * In the redesigned flow, both fields are optional for all non-VIN mutations.
 */
export const buildOptionalFields = (condition?: string, yearOfManufacture?: string): string => {
  const parts: string[] = [];
  if (condition) parts.push(`condition: ${condition}`);
  if (yearOfManufacture) {
    const year = new Date(yearOfManufacture).getFullYear();
    if (!Number.isNaN(year)) parts.push(`yearOfManufacture: ${year}`);
  }
  return parts.join(', ');
};

/**
 * Resolves which mutation and params to use for AGCO brands.
 *
 *   1. modelVariant (real, not custom) → createDeviceAgcoByModelVariant
 *   2. model (real, not custom)        → createDeviceAgcoByModel
 *   3. serie (real, not custom)        → createDeviceAgcoByModelSeries
 *   4. subCategory available           → createDeviceAgcoBySubCategory
 *   5. brand only                      → createDeviceByBrandAndModelText
 */
export const resolveAgcoMutation = (nonVinData: NonVinData): IMutationConfig => {
  const {
    brand,
    subCategory,
    serie,
    model,
    modelVariant,
    customSeriesOption,
    customModelsOption,
    customModelVariantsOption,
    customSerieName,
    customModelName,
    customModelVariantName,
  } = nonVinData;

  if (isRealUUID(modelVariant?.id) && !customModelVariantsOption?.id) {
    return { mutationName: 'createDeviceAgcoByModelVariant', deviceParams: `modelVariantId: "${modelVariant?.id}"` };
  }

  if (isRealUUID(model?.id) && !customModelsOption?.id) {
    const variantText = customModelVariantsOption?.name || customModelVariantName || '';
    const parts = [`modelId: "${model?.id}"`];
    if (variantText) parts.push(`modelVariantText: "${variantText}"`);
    return { mutationName: 'createDeviceAgcoByModel', deviceParams: parts.join(', ') };
  }

  if (isRealUUID(serie?.id) && !customSeriesOption?.id) {
    const modelText = customModelsOption?.name || customModelName || '';
    const parts = [`modelSeriesId: "${serie?.id}", modelText: "${modelText}"`];
    if (customModelVariantName) parts.push(`modelVariantText: "${customModelVariantName}"`);
    return { mutationName: 'createDeviceAgcoByModelSeries', deviceParams: parts.join(', ') };
  }

  if (isRealUUID(subCategory?.id)) {
    const seriesText = customSeriesOption?.name || customSerieName || '';
    const modelText = customModelName || '';
    const parts = [`brandId: "${brand?.id}", subCategoryId: "${subCategory?.id}", modelText: "${modelText}"`];
    if (seriesText) parts.push(`modelSeriesText: "${seriesText}"`);
    return { mutationName: 'createDeviceAgcoBySubCategory', deviceParams: parts.join(', ') };
  }

  if (brand?.id) {
    return {
      mutationName: 'createDeviceByBrandAndModelText',
      deviceParams: `brandId: "${brand.id}", modelText: "${customModelName || model?.name || ''}"`,
    };
  }

  throw new Error('Insufficient data for creating AGCO device');
};

/**
 * Resolves which mutation and params to use for non-AGCO brands.
 *
 *   1. subCategory available → createDeviceNonAgco
 *   2. brand only            → createDeviceByBrandAndModelText
 */
export const resolveNonAgcoMutation = (nonVinData: NonVinData): IMutationConfig => {
  const { brand, subCategory, customModelName } = nonVinData;
  const modelText = customModelName || '';

  if (isRealUUID(subCategory?.id) && brand?.id) {
    return {
      mutationName: 'createDeviceNonAgco',
      deviceParams: `brandId: "${brand.id}", subCategoryId: "${subCategory?.id}", modelText: "${modelText}"`,
    };
  }

  if (brand?.id) {
    return {
      mutationName: 'createDeviceByBrandAndModelText',
      deviceParams: `brandId: "${brand.id}", modelText: "${modelText}"`,
    };
  }

  throw new Error('Insufficient data for creating non-AGCO device');
};
